import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    setToken(null);
    setAdmin(null);
  }, []);

  const loadAdmin = useCallback(async () => {
    try {
      const data = await authAPI.getMe();
      setAdmin(data);
    } catch (error) {
      console.error('Failed to load admin:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      loadAdmin();
    } else {
      setLoading(false);
    }
  }, [token, loadAdmin]);

  const login = useCallback(async (email, password) => {
    const data = await authAPI.login(email, password);
    localStorage.setItem('admin_token', data.access_token);
    localStorage.setItem('admin_data', JSON.stringify(data.admin));
    setToken(data.access_token);
    setAdmin(data.admin);
    return data;
  }, []);

  const isSuperAdmin = useCallback(() => {
    return admin?.role === 'super_admin';
  }, [admin]);

  const value = useMemo(
    () => ({
      admin,
      loading,
      isAuthenticated: !!admin,
      isSuperAdmin,
      login,
      logout,
    }),
    [admin, loading, isSuperAdmin, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
